// MpfInputBloodPressure.props.ts
// Not an NHS Design System component. Built on their foundations — two Text inputs
// with inputmode="numeric" inside a fieldset with a legend:
// https://service-manual.nhs.uk/design-system/components/text-input
import type { HeadingLevel, HeadingLevelSize } from '../MpfInput/MpfInputBaseProps';
import { MpfInputBaseProps, MpfInputEmits } from '../MpfInput/MpfInputBaseProps';

export interface MpfInputBloodPressureProps extends Omit<
  MpfInputBaseProps,
  'modelValue' | 'headingLevel' | 'headingLevelSize'
> {
  type?: 'bloodPressure';
  // Systolic over diastolic, e.g. "120/80". Follows MpfInputDate, which flattens its
  // parts to a string rather than widening the shared FormFieldValue union.
  modelValue?: string;
  legendHeadingLevel?: HeadingLevel;
  legendHeadingSize?: HeadingLevelSize;
  legendFontWeight?: 'bold' | 'normal';
  systolicLabel?: string;
  diastolicLabel?: string;
}

// Defaults MUST be values (NOT factories) when using withDefaults
export const mpfInputBloodPressureDefaults = {
  required: false,
  modelValue: '',
  ariaDescribedby: '',
  type: 'bloodPressure',
  legendHeadingLevel: 2 as HeadingLevel,
  legendHeadingSize: 'l' as HeadingLevelSize,
  systolicLabel: 'Systolic',
  diastolicLabel: 'Diastolic',
  // @ts-expect-error
  errors: () => [] as string[],
} satisfies Partial<MpfInputBloodPressureProps>;

export type MpfInputBloodPressureEmits = MpfInputEmits;

export interface BloodPressureParts {
  systolic: string;
  diastolic: string;
}

/**
 * Split the component's "120/80" model back into the two values the filing
 * handler reads. The submit payload carries systolic and diastolic separately —
 * a combined string would not file.
 */
export function bloodPressureParts(value?: string): BloodPressureParts {
  const [systolic = '', diastolic = ''] = (value ?? '').split('/');
  return { systolic: systolic.trim(), diastolic: diastolic.trim() };
}
